import styled from 'styled-components';
import { PopupWrapped, ContentWrapper } from '../../../../general_components/popup_manager/popupManager';
import {RemoveScroll} from 'react-remove-scroll';
import { InformationCard} from '../../../../general_components/content_management/item_page/informationCard';
import { TRUCK_POPUP_OBJECT } from '../../../../pages/trucks/truckPopupConf';
import { OverviewCard, GenerateEditableSection } from '../../../../general_components/content_management/item_page/overViewCard';
import { convertNotesToArray, NoteManagingComponent } from '../../../../general_components/notes/noteManagingComponent';
import { TasksBlock } from '../../../../pages/trucks/truckPageConfComponents';
import { LOAD_PAGE_OBJECT } from '../../../../pages/loads/loadPageConf';
import { NestedValueExtractor } from '../../../../general_components/content_management/list_view/listView';
import { loadStatusFormatter, loadStatusColorGenerator } from '../../../../pages/loads/loadDataDelegate';
import { CustomLoadingIcon } from '../../../../supplementary_components/loading_icon';
import { useBoardStore } from '../../../boardStore';

const PopupFrame = styled.div`
    width: 100%;
    padding: 1.5rem 1rem 1.5rem 1.5rem;
    overflow-y: scroll;

    &.load-layout {
        display: grid;
        grid-template-columns: 70% 1fr;
        gap: 1rem;

        & > .infoBox > .details.loadpopup {
            display: grid;
            grid-template-columns: 30% minmax(0, 1fr);
            gap: 1rem;
        }

        & > .notesBox {
            position: sticky;
            top: 1px;
            height: 700px;
        }
    }

    &.truck-layout {
        display: block;

        & > .infoBox {
            display: grid;
            /* 39.8% of the former 70% info column — same InformationCard width as before */
            grid-template-columns: calc(70% * 0.396) minmax(0, 1fr);
            gap: 1rem;
            align-items: start;

            & > .infoColumn {
                min-width: 0;
            }

            & > .truckRightColumn {
                display: flex;
                flex-direction: column;
                gap: 0rem;
                min-width: 0;

                & > .truckMiddleRow {
                    display: grid;
                    grid-template-columns:55% 45%;
                    gap: 1rem;
                    align-items: stretch;

                    & > .overviewColumn {
                        min-width: 0;
                    }

                    & > .notesBox {
                        position: static;
                        min-height: 0;
                        display: flex;
                        flex-direction: column;
                        overflow: hidden;
                        padding: 0.1rem;
                    }
                }
            }
        }
    }
`;

export const LoadingFrame = styled.div`
    height:100%;
    display:flex;
    justify-content:center;
    align-items:center;
`;

export const PopupWindow = () => {
    const popupSelection = useBoardStore(s => s.popupSelection);
    const closePopup = useBoardStore(s => s.closePopup);
    const selectedFromStore = useBoardStore(s => {
        const sel = s.popupSelection;
        if(!sel) return null;
        if(sel.type === 'load') return s.loadsById?.[sel.id] || null;
        if(sel.type === 'truck') return s.trucksById?.[sel.truckId] || null;
        if(sel.type === 'raw') return sel.obj || null;
        return null;
    });

    // If the selection is a store reference but the entity isn't loaded yet, keep showing a loading frame.
    const effectiveSelectedObject = (popupSelection && popupSelection.type !== 'raw' && !selectedFromStore)
        ? { loadNumber: popupSelection.type === 'load', loading: true }
        : selectedFromStore;
    const pageObject = effectiveSelectedObject ? effectiveSelectedObject.loadNumber ? new LOAD_PAGE_OBJECT(NestedValueExtractor, loadStatusFormatter, loadStatusColorGenerator) : new TRUCK_POPUP_OBJECT() : null;
    if(pageObject){
        if(!effectiveSelectedObject.loading){
            pageObject.setData(effectiveSelectedObject);
        }
    }

    const isLoadPopup = Boolean(effectiveSelectedObject?.loadNumber);
    const noopSetData = () => {};

    const notesElement = pageObject ? (
        <NoteManagingComponent
            subjectsToAddNote={isLoadPopup ? null : {
                truck: { id: effectiveSelectedObject.id, label: effectiveSelectedObject.truckNumber, type: "trucks" },
                driver1: pageObject.firstDriver,
                driver2: pageObject.secondDriver,
                currentTrailer: pageObject.currentTrailer,
            }}
            notesArray={isLoadPopup ? convertNotesToArray(pageObject.notes) : pageObject.notes}
            category={isLoadPopup ? 'loads' : ['trucks', 'drivers', "trailers"]}
            itemId={pageObject.id}
            fullScreen={true}
            fillParent={true}
            popup={true}
        />
    ) : null;

    return <>
        {effectiveSelectedObject ? 
        <RemoveScroll>
            <PopupWrapped data-closable={false}
                onMouseDown={(e)=>{
                    e.target.setAttribute("data-closable", true);
                }} 
                onClick={(e)=>{
                    if(e.target.dataset.closable === 'true'){
                        closePopup();
                    }
                }} 
            >
                <ContentWrapper style={{height:"fit-content", maxHeight: "90%", overflowY:"scroll"}} onClick={e=>e.stopPropagation()} $customWidth={"90%"} $customMaxWidth={"1400px"} $customMinWidth={"780px"} >
                    {effectiveSelectedObject.loading ? 
                        <LoadingFrame><CustomLoadingIcon height={'2.5rem'} speed={1.0} /></LoadingFrame>
                    :
                    <PopupFrame className={isLoadPopup ? 'load-layout' : 'truck-layout'}>
                        {isLoadPopup ? (
                            <>
                                <div className="infoBox">
                                    <div className="details loadpopup">
                                        <InformationCard object={pageObject} selectedObject={effectiveSelectedObject} setData={noopSetData} />
                                        <OverviewCard object={pageObject} setData={noopSetData} />
                                    </div>
                                </div>
                                <div
                                    className="notesBox"
                                >
                                    {notesElement}
                                </div>
                            </>
                        ) : (
                            <div className="infoBox">
                                <div className="infoColumn">
                                    <InformationCard object={pageObject} selectedObject={effectiveSelectedObject} setData={noopSetData} />
                                </div>
                                <div className="truckRightColumn">
                                <div className="truckMiddleRow">
                                    <div className="overviewColumn">
                                        <OverviewCard object={pageObject} setData={noopSetData} />
                                    </div>
                                    <div className="notesBox">{notesElement}</div>
                                </div>
                                <GenerateEditableSection
                                    section={TasksBlock(pageObject)}
                                    setData={noopSetData}
                                    smallerFont={true}
                                    wrapInBox={true}
                                />
                                </div>
                            </div>
                        )}
                    </PopupFrame>
                    }
                </ContentWrapper>
            </PopupWrapped>
        </RemoveScroll>
    :
    null}
    </>
};
